import React, { useState, useEffect } from 'react';
import ForecastControl from './ForecastControl';
import ScheduleTiersCard from './ScheduleTiersCard';
import ScheduleTiersModal from './ScheduleTiersModal';
import LookbackConfigCard from './LookbackConfigCard';
import CostEstimateDialog from './CostEstimateDialog';
import { usePresets, useEstimateCalls, applySchedulePreset, applyLookbackPreset, DEFAULT_SYSTEM_CONFIG } from '../../hooks/useSystemConfig';

export default function PricingSystemConfig({ systemConfig: initialConfig, onSave, isSaving }) {
  const [config, setConfig] = useState(initialConfig || DEFAULT_SYSTEM_CONFIG);
  const [showTiersModal, setShowTiersModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: presets } = usePresets();
  const estimateMutation = useEstimateCalls();

  useEffect(() => {
    if (initialConfig) setConfig(initialConfig);
  }, [initialConfig]);

  const handleChange = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleScheduleTiersChange = (newTiers) => {
    setConfig(prev => ({ ...prev, schedule_tiers: newTiers }));
    setHasChanges(true);
  };

  const handleSchedulePresetChange = (presetName) => {
    const updated = applySchedulePreset(presets, presetName, config);
    setConfig(updated);
    setHasChanges(true);
  };

  const handleLookbackChange = (updates) => {
    setConfig(prev => ({
      ...prev,
      lookback_config: { ...prev.lookback_config, preset: 'custom', ...updates },
    }));
    setHasChanges(true);
  };

  const handleLookbackPresetChange = (presetName) => {
    const updated = applyLookbackPreset(presets, presetName, config);
    setConfig(updated);
    setHasChanges(true);
  };

  const handleSaveClick = () => {
    estimateMutation.mutate(config);
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    onSave({ system_config: config });
    setShowConfirmDialog(false);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Pricing System Configuration</h2>
        {hasChanges && (
          <button
            onClick={handleSaveClick}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ForecastControl
          forecastDays={config.forecast_days}
          scheduleEnabled={config.schedule_enabled}
          onChange={handleChange}
        />
        <ScheduleTiersCard
          scheduleTiers={config.schedule_tiers}
          presets={presets?.schedule_presets}
          onPresetChange={handleSchedulePresetChange}
          onEditClick={() => setShowTiersModal(true)}
        />
      </div>

      <LookbackConfigCard
        lookbackConfig={config.lookback_config}
        presets={presets?.lookback_presets}
        onPresetChange={handleLookbackPresetChange}
        onChange={handleLookbackChange}
      />

      <ScheduleTiersModal
        isOpen={showTiersModal}
        onClose={() => setShowTiersModal(false)}
        scheduleTiers={config.schedule_tiers}
        presets={presets?.schedule_presets}
        onSave={handleScheduleTiersChange}
      />

      <CostEstimateDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmSave}
        estimate={estimateMutation.data}
        isLoading={estimateMutation.isPending}
      />
    </div>
  );
}
